import { useState } from 'react'
import { MessageSquare, Send, ChevronLeft } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { Input } from '@components/ui/input'
import { usePortalMessages, useSendPortalMessage } from '@hooks/usePortal'
import { useToast } from '@hooks/use-toast'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { cn } from '@lib/utils'

export function PortalMessagesPage() {
  const { toast } = useToast()
  const { formatDateTime } = useClinicFormat()
  const { data: messagesData, isLoading } = usePortalMessages()
  const sendMessage = useSendPortalMessage()

  const [showCompose, setShowCompose] = useState(false)
  const [subject, setSubject] = useState('')
  const [content, setContent] = useState('')

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await sendMessage.mutateAsync({
        subject,
        content,
        priority: 'normal',
      })
      toast({
        title: 'Mensaje enviado',
        description: 'Tu mensaje fue enviado al equipo médico.',
      })
      setShowCompose(false)
      setSubject('')
      setContent('')
    } catch (error) {
      toast({
        title: 'No se pudo enviar',
        description: 'Inténtalo de nuevo más tarde.',
        variant: 'destructive',
      })
    }
  }

  const messages = messagesData?.items || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mensajes</h1>
          <p className="text-muted-foreground mt-1">Comunícate con tu equipo de atención</p>
        </div>
        {!showCompose && (
          <Button onClick={() => setShowCompose(true)}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Nuevo mensaje
          </Button>
        )}
      </div>

      {showCompose ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowCompose(false)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <CardTitle>Nuevo mensaje</CardTitle>
            </div>
            <CardDescription>Envía un mensaje a tu equipo médico</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendMessage} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Asunto</label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="¿Sobre qué tema es este mensaje?"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Mensaje</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Escribe tu mensaje aquí..."
                  className="w-full px-3 py-2 rounded-md border bg-background min-h-[150px]"
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowCompose(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={sendMessage.isPending}>
                  <Send className="h-4 w-4 mr-2" />
                  {sendMessage.isPending ? 'Enviando...' : 'Enviar mensaje'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="h-24 animate-pulse"></Card>
          ))}
        </div>
      ) : !messages.length ? (
        <Card className="p-8 text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Sin mensajes</h3>
          <p className="text-muted-foreground">No tienes mensajes en tu bandeja de entrada.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <Card key={message.id} className={cn(!message.is_read && 'border-medical-300')}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-medical-100 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="h-5 w-5 text-medical-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={cn('font-semibold truncate', !message.is_read && 'text-medical-700')}>
                        {message.subject}
                      </h3>
                      {!message.is_read && (
                        <span className="h-2 w-2 rounded-full bg-medical-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">From: {message.sender_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDateTime(message.sent_at)}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'px-2 py-0.5 rounded text-xs font-medium',
                      message.priority === 'urgent' ? 'bg-red-100 text-red-800' : 'bg-gray-100'
                    )}
                  >
                    {message.priority}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
